<template>
  <b-card v-if="isPermitted('seeAppeals')">

    <div>
      <b-row>
        <!-- Project -->
        <b-col>
          <v-select
            id="vue-select-project"
            v-model="selectedProject"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="optionProject"
            :reduce="project => project.id"
            label="projectName"
            :placeholder="$t('All projects')"
            :clearable="false"
            class="mb-2"
          />
        </b-col>
        <!-- Period start -->
        <b-col md="2">
          <v-select
            v-if="selectedProject"
            id="vue-select-period-start"
            v-model="selectedPeriodStart"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="optionProjectPeriods"
            :clearable="false"
            class="mb-2"
          />

        </b-col>
        <!-- Period end -->
        <b-col md="2">
          <v-select
            v-if="selectedProject"
            id="vue-select-period-end"
            v-model="selectedPeriodEnd"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="optionProjectPeriods"
            :clearable="false"
            class="mb-2"
          />
        </b-col>
        <!-- Approve Status -->
        <b-col md="2">
          <v-select
            v-if="selectedProject"
            id="vue-select-appeal-status"
            v-model="appealStatus"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="optionAppealStatus"
            :reduce="item => item.value"
            class="mb-2"
          />
        </b-col>
        <b-col md="2">
          <b-button
            class="w-100"
            :variant="appliedFilter && appliedFilter.filters.length ? 'success' : 'secondary'"
            @click="hotalertFilter"
          >
            {{ $t('Advanced Filter') }}
          </b-button>
        </b-col>
      </b-row>

      <b-row v-if="appliedFilter && appliedFilter.filters.length">
        <b-col>
          <b-card>
            <span
              v-for="(filterItem, index) in appliedFilter.filters"
              :key="index"
            >
              <template v-if="filterItem.operator">
                {{ filterItem.operator }}
              </template>

              <template v-else>
                <strong class="mr-1">{{ filterItem.filterQuestion.questionText }} = </strong>
                <b-badge
                  v-for="(value, idx) in filterItem.filterValues"
                  :key="idx"
                  variant="primary"
                >{{
                  value.optionText
                }}</b-badge>
                <b-badge
                  v-for="(value, idx) in filterItem.filterValuesText"
                  :key="idx"
                  variant="primary"
                >{{ value
                }}</b-badge>
              </template>
            </span>

          </b-card>

        </b-col>
      </b-row>

      <b-form
        inline
        class="mb-2"
      >

        <b-form-input
          v-model="searchTerm"
          :placeholder="$t('message.SearchPlaceholder')"
          type="text"
          class="d-inline mt-1"
        />

        <b-link
          class="ml-2 mt-1"
          @click="downloadAppeals"
        >
          Download
        </b-link>

        <!-- Hidden file input -->
        <input
          id="hidden-file-input"
          ref="fileInput"
          type="file"
          style="display: none;"
          accept=".xlsx"
          @change="handleFileUpload"
        >

        <!-- Upload link -->
        <b-link
          class="ml-2 mt-1"
          @click="triggerFileInput"
        >
          Upload
        </b-link>

      </b-form>

      <!-- table -->
      <vue-good-table
        style-class="vgt-table striped"
        :columns="columns"
        :rows="rows"
        :sort-options="{
          enabled: true,
          initialSortBy: { field: 'id', type: 'desc' }
        }"
        :search-options="{
          enabled: true,
          externalQuery: searchTerm
        }"
        :select-options="{
          enabled: false,
          selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
          selectionInfoClass: 'custom-class',
          selectionText: 'rows selected',
          clearSelectionText: 'clear',
          disableSelectInfo: true, // disable the select info panel on top
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
          mode: 'records',
          perPage: 10,
          perPageDropdown: [10, 50, 100],
          dropdownAllowAll: false,
        }"
      >

        <template
          slot="table-column"
          slot-scope="props"
        >
          <span>
            {{ $t(props.column.label) }}
          </span>
        </template>

        <template
          slot="table-row"
          slot-scope="props"
        >

          <!-- Column: OrjRef -->
          <span
            v-if="props.column.field === 'orjref'"
            class="text-nowrap"
          >
            <b-link @click="view(props.row)">
              {{ props.row.orjref }}
            </b-link>
          </span>

          <!-- Column: createDate -->
          <span
            v-else-if="props.column.field === 'appealdate'"
            class="text-nowrap"
          >
            {{ formatDate(props.row.appealdate) }}
          </span>

          <!-- Column: Common -->
          <span v-else>
            {{ $t(props.formattedRow[props.column.field]) }}
          </span>
        </template>

      </vue-good-table>

      <modal-survey
        ref="surveyModal"
        :boxid="0"
        :appeal="true"
        :appeal-management="true"
        @reload="reload"
      />

      <modal-hot-alert-filter
        v-if="selectedProject"
        v-model="filterConfig"
        :project-id="selectedProject"
        @refreshHotalertList="setFilter"
      />

    </div>
  </b-card>
</template>

<script>

import { getUserData, isPermitted } from '@/auth/utils'
import useJwt from '@/auth/jwt/useJwt'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import {
  BCard, BForm, BFormInput, BLink, BRow, BCol, BButton, BBadge,
} from 'bootstrap-vue'
import ModalSurvey from '@/layouts/components/ModalSurvey.vue'
import ModalHotAlertFilter from '@/layouts/components/ModalHotAlertFilter.vue'
import dayjs from 'dayjs'

export default {
  components: {
    vSelect,
    BCard,
    BForm,
    BFormInput,
    BRow,
    BCol,
    BLink,
    BButton,
    BBadge,
    VueGoodTable,
    ModalSurvey,
    ModalHotAlertFilter,
  },
  data() {
    return {
      file: null,
      appealStatus: 'All Appeals',
      pageLength: 10,
      listColumns: [],
      rows: [],
      searchTerm: '',
      preColumns: [
        {
          label: '#',
          field: 'id',
          type: 'number',
          tdClass: 'text-center',
          thClass: 'text-center',
          width: '50px',
        },
        {
          label: 'Survey Id',
          field: 'orjref',
          type: 'number',
        },
        {
          label: 'Appeal Date',
          field: 'appealdate',
        },
        {
          label: 'Appeal By',
          field: 'appealby',
        },
        {
          label: 'Appeal Reason',
          field: 'appealreason',
        },
      ],
      postColumns: [
        {
          label: 'Status',
          field: 'status',
        },
      ],
      columns: [],
      stats: [],
      selectedProject: null,
      optionProject: [],
      optionProjectPeriods: [],
      optionAppealStatus: [
        { label: this.$t('All Appeals'), value: 'All Appeals' },
        { label: this.$t('Open Appeal'), value: 'Open Appeal' },
        { label: this.$t('Closed Appeal'), value: 'Closed Appeal' },
        { label: this.$t('Accepted'), value: 'Accepted' },
        { label: this.$t('Rejected'), value: 'Rejected' },
      ],
      selectedPeriodStart: null,
      selectedPeriodEnd: null,
      filterConfig: { filters: [] },
      appliedFilter: { filters: [] },
    }
  },
  watch: {
    selectedProject() {
      this.getProjectPeriodOptions()
    },
    selectedPeriodStart() {
      this.loadData()
    },
    selectedPeriodEnd() {
      this.loadData()
    },
    appealStatus() {
      this.loadData()
    },
  },
  created() {
    this.userData = getUserData()
    this.getProjectOptions()
  },
  methods: {
    triggerFileInput() {
      // Programmatically click the hidden file input
      this.$refs.fileInput.click()
    },

    handleFileUpload(event) {
      const file = event.target.files[0] // Get the selected file

      if (file) {
        this.uploadFile(file) // Upload the file
      }
    },

    async uploadFile(file) {
      const formData = new FormData()
      formData.append('AppealFile', file)
      formData.append('ProjectId', this.selectedProject)
      formData.append('Locale', this.$i18n.locale)

      // Loading message
      await this.$swal({
        title: this.$t('Processing file...'),
        allowOutsideClick: false,
        didOpen: async () => {
          this.$swal.showLoading()

          // Upload the file to the server
          useJwt.axiosIns.post('project/UploadAppeal', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
            .then(response => {
              if (response.data === 'Ok') {
                this.$swal({
                  icon: 'success',
                  title: this.$t('Success!'),
                  text: this.$t('Appeals updated successfuly.'),
                  customClass: {
                    confirmButton: 'btn btn-success',
                  },
                })
                this.loadData()
              } else {
                this.$swal({
                  icon: 'error',
                  title: this.$t('Error!'),
                  text: `Error: ${response.data}`,
                  customClass: {
                    confirmButton: 'btn btn-danger',
                  },
                })
              }
            })
            .catch(error => {
              // Handle error
              console.error('Error uploading file', error)
            })
        },
      })
    },
    isPermitted(code) {
      if (!this.userData) {
        return false
      }
      return isPermitted(this.userData.role, code, this.userData.userGroups)
    },
    view(item) {
      this.$refs.surveyModal.loadSurveyData(item.projectid, item.orjref)
    },
    getProjectOptions() {
      useJwt.axiosIns.get('project').then(response => {
        this.optionProject = response.data.filter(p => p.status)
        if (this.optionProject.length) {
          this.selectedProject = this.optionProject[0].id
          this.getProjectPeriodOptions()
        }
      })
    },
    getProjectPeriodOptions() {
      useJwt.axiosIns.get(`project/${this.selectedProject}/wavelist`).then(response => {
        this.optionProjectPeriods = response.data.sort((a, b) => b - a)
        if (response.data) {
          // eslint-disable-next-line prefer-destructuring
          this.selectedPeriodEnd = response.data[0]
          this.selectedPeriodStart = response.data[response.data.length - 1]
        }
      })
    },
    formatDate(date) {
      return dayjs(date).format('DD.MM.YYYY HH:mm:ss')
    },
    downloadAppeals() {
      useJwt.axiosIns.post(`project/${this.selectedProject}/exportxlsx/${this.$i18n.locale}/${this.selectedPeriodStart}/${this.selectedPeriodEnd}/${this.appealStatus}/appeals`, this.appliedFilter, { responseType: 'arraybuffer' })
        .then(response => {
          if (response) {
            this.openFile(response, `appeals_${this.selectedProject}.xlsx`)
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    openFile(response, filename) {
      const type = response.headers['content-type']
      const blob = new Blob([response.data], {
        type,
        encoding: 'UTF-8',
      })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
      link.remove()
    },
    async loadData() {
      if (this.selectedProject && this.selectedPeriodStart && this.selectedPeriodEnd) {
        useJwt.axiosIns.post(`project/${this.selectedProject}/${this.selectedPeriodStart}/${this.selectedPeriodEnd}/${this.appealStatus}/appeals`, this.appliedFilter)
          .then(async response2 => {
            if (response2.data) {
              this.rows = response2.data
              this.columns = [...this.preColumns, ...this.postColumns]
            }
          })
          .catch(e => {
            console.log(e)
          })
      }
    },
    hotalertFilter() {
      this.filterConfig = JSON.parse(JSON.stringify(this.appliedFilter))
      this.$root.$emit('hotalert_filter', this.filterConfig)
    },
    setFilter(filter) {
      this.appliedFilter = JSON.parse(JSON.stringify(filter))
      this.loadData()
    },
    reload() {
      this.loadData()
    },

  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
